/**
 * AGC: Tournament Match Component (PvPTourney)
 * AGC: Handles tournament-specific battle flow (NFT payment only, no OGB)
 * AGC: Entry fee already paid in tournament lobby
 * AGC: 1-minute timeout for opponent to join
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useWebSocket } from '../../hooks/useWebSocket'
import { useAccount } from 'wagmi'
import { ethers } from 'ethers'
import { getSafeSigner } from '../../utils/getBrowserProvider'
import { useFeatureFlags } from '../../hooks/useFeatureFlags'
import { InteractiveTournamentBattle } from './InteractiveTournamentBattle'
import { BuffSelector } from '../BuffSelector'
import type { ActivePage } from '../../MainMenu'

interface PvPTourneyProps {
  playerAddress: string
  playerLevel: number
  tournamentId: string
  opponentAddress: string
  opponentLevel: number
  matchData: {
    round: number
    matchNumber: number
  }
  preSelectedBuffs?: number[] // AGC: NFTs pre-selected in TournamentLobby
  onNavigateToPage?: (page: ActivePage) => void
  onMatchComplete: (winner: string, loser: string, turns: number, isDraw: boolean, battleId: string) => void
}

export const PvPTourney: React.FC<PvPTourneyProps> = ({
  playerAddress,
  playerLevel,
  tournamentId,
  opponentAddress,
  opponentLevel,
  matchData,
  preSelectedBuffs = [],
  onNavigateToPage,
  onMatchComplete
}) => {
  // AGC: State management - initialize with pre-selected buffs from TournamentLobby
  // AGC: Use preSelectedBuffs if provided, otherwise allow selection in this component
  const [selectedBuffs, setSelectedBuffs] = useState<number[]>(preSelectedBuffs || [])
  const [selectedPotions, setSelectedPotions] = useState<number[]>([])
  const [isPaying, setIsPaying] = useState(false)
  const [hasPaid, setHasPaid] = useState(false)
  const [battleStarted, setBattleStarted] = useState(false)
  const [battleId, setBattleId] = useState<string>('')
  const [timeRemaining, setTimeRemaining] = useState(60) // 1 minute timeout
  const [opponentTimedOut, setOpponentTimedOut] = useState(false)

  const { connector } = useAccount()
  const { isFarcasterContext } = useFeatureFlags()
  const { socket, confirmPayment } = useWebSocket()

  // AGC: Sync selectedBuffs with preSelectedBuffs when they change (e.g., from TournamentLobby)
  useEffect(() => {
    if (preSelectedBuffs && preSelectedBuffs.length > 0) {
      console.log('🏆 Pre-selected buffs received:', preSelectedBuffs)
      setSelectedBuffs(preSelectedBuffs)
    }
  }, [preSelectedBuffs])

  // AGC: Listen for match_found event from backend
  // AGC: Set up listener immediately on mount to catch events that arrive early
  useEffect(() => {
    if (!socket) {
      console.log('🏆 PvPTourney: Socket not ready yet, waiting...')
      return
    }

    console.log('🏆 PvPTourney: Setting up match_found listener for tournament:', tournamentId)

    const handleMatchFound = (data: any) => {
      console.log('[CLIENT] PvPTourney: match_found received:', data)
      // AGC: Filter by tournamentId AND battleId to avoid processing wrong matches
      if (data.tournamentMatch && data.tournamentId === tournamentId) {
        // AGC: If we already have a battleId, only process if it matches
        if (battleId && data.battleId !== battleId) {
          console.log('[CLIENT] PvPTourney: Ignoring match_found for different battle:', data.battleId, 'expected:', battleId)
          return
        }
        
        console.log('[CLIENT] PvPTourney: Setting battleId:', data.battleId)
        setBattleId(data.battleId)
        // AGC: Don't auto-confirm payment - let player select NFTs first, then manually confirm
        // AGC: This ensures NFT selector is always shown before payment
        console.log('[CLIENT] PvPTourney: Match found - waiting for player to select NFTs and confirm payment', { 
          preSelectedBuffs, 
          selectedBuffs 
        })
      } else {
        console.log('[CLIENT] PvPTourney: Ignoring match_found (not tournament match or wrong tournamentId)', {
          hasTournamentMatch: data.tournamentMatch,
          eventTournamentId: data.tournamentId,
          expectedTournamentId: tournamentId
        })
      }
    }

    console.log('🏆 PvPTourney: Registering match_found listener.')
    socket.on('match_found', handleMatchFound)
    console.log('🏆 PvPTourney: match_found listener registered')

    return () => {
      console.log('🏆 PvPTourney: Unregistering match_found listener.')
      socket.off('match_found', handleMatchFound)
    }
  }, [socket, tournamentId, battleId, preSelectedBuffs, selectedBuffs])

  // AGC: Listen for battle_start event
  useEffect(() => {
    if (!socket) return

    const handleBattleStart = (data: any) => {
      console.log('[CLIENT] PvPTourney: battle_start received:', data)
      // AGC: Match by battleId OR tournamentId (in case battleId not set yet)
      if (data.battleId === battleId || (data.tournamentId === tournamentId && battleId && data.battleId === battleId)) {
        console.log('[CLIENT] PvPTourney: Battle start confirmed - starting tournament battle')
        setBattleStarted(true)
        setTimeRemaining(0) // Stop timeout countdown
      } else {
        console.log('[CLIENT] PvPTourney: Ignoring battle_start (battleId mismatch)', {
          received: data.battleId,
          expected: battleId,
          tournamentIdMatch: data.tournamentId === tournamentId
        })
      }
    }

    console.log('🏆 PvPTourney: Registering battle_start listener.')
    socket.on('battle_start', handleBattleStart)

    return () => {
      console.log('🏆 PvPTourney: Unregistering battle_start listener.')
      socket.off('battle_start', handleBattleStart)
    }
  }, [socket, battleId, tournamentId])

  // AGC: Step 3: Handle NFT selection (separate buffs from potions)
  const handleBuffSelection = useCallback((combinedItems: number[]) => {
    // AGC: Separate buffs (0-3) from potions (10000+) in combined array
    const buffs: number[] = []
    const potions: number[] = []
    
    for (const item of combinedItems) {
      if (item >= 10000) {
        // AGC: Potion (offset by 10000)
        potions.push(item - 10000)
      } else {
        // AGC: Buff (0-3)
        buffs.push(item)
      }
    }
    
    console.log('🏆 [handleBuffSelection] Received combined items:', combinedItems)
    console.log('🏆 [handleBuffSelection] Split into buffs:', buffs, 'potions:', potions)
    
    setSelectedBuffs(buffs)
    setSelectedPotions(potions)
  }, [])

  // AGC: Step 4: Pay NFT ONLY (no OGB - already paid in tournament entry)
  // AGC: For tournament matches, we escrow NFTs directly without calling payBattleStakeWithNFTs
  // AGC: (which requires OGB). Backend handles marking player as paid.
  const handlePayNFT = useCallback(async () => {
    if (!battleId) {
      console.warn('🏆 Battle ID not available yet - waiting for match_found')
      return
    }

    // AGC: CRITICAL - Log state at time of execution for debugging
    console.log('🏆 [handlePayNFT] Current state:', {
      selectedBuffs,
      selectedPotions,
      totalNFTs: selectedBuffs.length + selectedPotions.length
    })

    // AGC: If no NFTs selected, just confirm payment (no on-chain transaction needed)
    if (selectedBuffs.length === 0 && selectedPotions.length === 0) {
      console.log('🏆 No NFTs selected - confirming payment without transaction')
      setHasPaid(true)
      confirmPayment(battleId, playerAddress, '0x0000000000000000000000000000000000000000000000000000000000000000', [])
      return
    }

    setIsPaying(true)

    try {
      const p2pManagerAddress = import.meta.env.VITE_P2P_MANAGER_ADDRESS
      const buff1155Address = import.meta.env.VITE_BUFF1155_ADDRESS
      const classicPotion1155Address = import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS

      if (!p2pManagerAddress) {
        throw new Error('P2P Manager address not configured')
      }

      const signer = await getSafeSigner({
        connector,
        isFarcasterContext,
        expectedAddress: playerAddress as `0x${string}`
      })

      const erc1155Abi = [
        'function setApprovalForAll(address operator, bool approved) external',
        'function isApprovedForAll(address account, address operator) external view returns (bool)',
        'function safeTransferFrom(address from, address to, uint256 id, uint256 amount, bytes calldata data) external'
      ]

      const allTransferTxs: any[] = []

      // AGC: Step 1: Approve and escrow buff NFTs if selected (SEQUENTIAL to avoid wallet issues)
      if (selectedBuffs.length > 0) {
        if (!buff1155Address) {
          throw new Error('Buff1155 address not configured')
        }

        console.log(`🏆 Processing ${selectedBuffs.length} buff(s):`, selectedBuffs)
        const buff1155 = new ethers.Contract(buff1155Address, erc1155Abi, signer)

        const isBuffApproved = await buff1155.isApprovedForAll(playerAddress, p2pManagerAddress)
        if (!isBuffApproved) {
          console.log('🏆 Approving buff NFT transfer to P2P Manager...')
          const approveTx = await buff1155.setApprovalForAll(p2pManagerAddress, true)
          try {
            await Promise.race([
              approveTx.wait(),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
              )
            ])
            console.log('✅ Buff NFT approval confirmed')
          } catch (waitError: any) {
            if (waitError.message?.includes('timeout')) {
              console.warn(`  ⚠️  Buff approval timeout (TX sent: ${approveTx.hash}) - continuing...`)
            } else {
              throw waitError
            }
          }
        }

        // AGC: Transfer each buff NFT SEQUENTIALLY (one wallet prompt at a time)
        for (const nftId of selectedBuffs) {
          console.log(`  🛡️ Transferring buff NFT ${nftId} to P2P Manager...`)
          const tx = await buff1155.safeTransferFrom(playerAddress, p2pManagerAddress, nftId, 1, '0x')
          console.log(`  ✅ Buff ${nftId} transfer submitted: ${tx.hash}`)
          allTransferTxs.push(tx)
        }
      }

      // AGC: Step 2: Approve and escrow potion NFTs if selected (SEQUENTIAL)
      if (selectedPotions.length > 0) {
        if (!classicPotion1155Address) {
          throw new Error('Potion1155 address not configured')
        }

        console.log(`🏆 Processing ${selectedPotions.length} potion(s):`, selectedPotions)
        const potion1155 = new ethers.Contract(classicPotion1155Address, erc1155Abi, signer)

        const isPotionApproved = await potion1155.isApprovedForAll(playerAddress, p2pManagerAddress)
        if (!isPotionApproved) {
          console.log('🏆 Approving potion NFT transfer to P2P Manager...')
          const approveTx = await potion1155.setApprovalForAll(p2pManagerAddress, true)
          try {
            await Promise.race([
              approveTx.wait(),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
              )
            ])
            console.log('✅ Potion NFT approval confirmed')
          } catch (waitError: any) {
            if (waitError.message?.includes('timeout')) {
              console.warn(`  ⚠️  Potion approval timeout (TX sent: ${approveTx.hash}) - continuing...`)
            } else {
              throw waitError
            }
          }
        }

        // AGC: Transfer each potion NFT SEQUENTIALLY
        for (const nftId of selectedPotions) {
          console.log(`  🧪 Transferring potion NFT ${nftId} to P2P Manager...`)
          const tx = await potion1155.safeTransferFrom(playerAddress, p2pManagerAddress, nftId, 1, '0x')
          console.log(`  ✅ Potion ${nftId} transfer submitted: ${tx.hash}`)
          allTransferTxs.push(tx)
        }
      }

      // AGC: Step 3: Wait for all transfers to confirm (with timeout handling)
      console.log(`🏆 Waiting for ${allTransferTxs.length} NFT transfer(s) to confirm...`)
      try {
        await Promise.all(
          allTransferTxs.map(tx => 
            Promise.race([
              tx.wait(),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
              )
            ])
          )
        )
        console.log('[OK] All NFTs escrowed successfully!')
      } catch (waitError: any) {
        if (waitError.message?.includes('timeout')) {
          console.warn(`  ⚠️  Some NFT transfers timed out, but transactions were sent - continuing...`)
          // AGC: Log which transactions were sent
          allTransferTxs.forEach((tx, idx) => {
            console.log(`  TX ${idx + 1}: ${tx.hash}`)
          })
        } else {
          throw waitError
        }
      }

      // AGC: Use first transaction hash as payment confirmation hash
      const paymentTxHash = allTransferTxs[0]?.hash || '0x0000000000000000000000000000000000000000000000000000000000000000'

      // AGC: Pass combined NFTs (buffs + potions with offset) to confirmPayment
      const combinedNFTs = [
        ...selectedBuffs,
        ...selectedPotions.map(id => 10000 + id) // AGC: Offset potion IDs
      ]

      setHasPaid(true)
      confirmPayment(battleId, playerAddress, paymentTxHash, combinedNFTs)

    } catch (error: any) {
      console.error('❌ NFT escrow failed:', error)
      // AGC: Better error message for user rejections
      if (error.code === 'ACTION_REJECTED' || error.code === 4001 || error.message?.includes('user rejected')) {
        alert('Transaction cancelled. Please approve the transaction in your wallet to continue.')
      } else {
        alert(`NFT escrow failed: ${error.message || 'Unknown error'}`)
      }
    } finally {
      setIsPaying(false)
    }
  }, [battleId, selectedBuffs, selectedPotions, playerAddress, connector, isFarcasterContext, confirmPayment])

  // AGC: Step 5: Wait for opponent (1 minute timeout)
  useEffect(() => {
    if (!hasPaid || battleStarted) return

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // AGC: Timeout - opponent didn't pay, player wins by default
          if (!opponentTimedOut && socket) {
            console.log('🏆 Opponent timed out - reporting win by default')
            setOpponentTimedOut(true)
            socket.emit('tournament_match_timeout', {
              battleId,
              tournamentId,
              playerAddress,
              opponentAddress
            })
            // AGC: Report match complete with player as winner
            onMatchComplete(playerAddress, opponentAddress, 0, false, battleId || '')
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [hasPaid, battleStarted, battleId, tournamentId, playerAddress, opponentAddress, socket, opponentTimedOut, onMatchComplete])

  // AGC: Check for existing battle state on mount (reconnection)
  // AGC: Call this even if battleId is not set yet - backend will check for pending matches
  useEffect(() => {
    if (!socket || !playerAddress || !tournamentId) return

    // AGC: Request reconnection check from backend
    // AGC: Backend will check for pending matches and re-emit match_found if battle exists
    console.log('🏆 Requesting tournament reconnect check:', { playerAddress, tournamentId, battleId: battleId || 'not set yet' })
    socket.emit('check_tournament_reconnect', {
      playerAddress,
      tournamentId,
      battleId: battleId || undefined // AGC: Pass undefined if not set - backend will find it
    })
  }, [socket, playerAddress, tournamentId]) // AGC: Don't include battleId in deps - we want to call this on mount

  // AGC: Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  // AGC: Step 7: Render Interactive Tournament Battle when battle starts
  if (battleStarted && battleId) {
    return (
      <InteractiveTournamentBattle
        battleId={battleId}
        tournamentId={tournamentId}
        playerAddress={playerAddress}
        opponentAddress={opponentAddress}
        opponentLevel={opponentLevel}
        socket={socket}
        selectedBuffs={selectedBuffs}
        selectedPotion={selectedPotions.length > 0 ? selectedPotions[0] : null} // AGC: Pass first potion (only 1 allowed)
        onBattleEnd={(won, turns, isDraw) => {
          // AGC: Step 8: Report result back to tournament
          // AGC: resolutionTxHash parameter is part of callback signature but not needed here
          const winner = won ? playerAddress : opponentAddress
          const loser = won ? opponentAddress : playerAddress
          onMatchComplete(winner, loser, turns, isDraw || false, battleId)
        }}
        onBackToMenu={() => {
          if (onNavigateToPage) onNavigateToPage('tournament')
        }}
      />
    )
  }

  // AGC: UI: Match found + NFT selector + payment
  const sharedTopPaddingStyle = { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 72px)' }

  return (
    <main
      className="pb-[88px] px-3 text-white flex flex-col gap-1.5"
      style={sharedTopPaddingStyle}
    >
      {/* AGC: Header */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">🏆 TOURNAMENT MATCH</p>
            <h1 className="text-2xl font-bold leading-tight text-white">
              Round {matchData.round} - Match {matchData.matchNumber}
            </h1>
            <p className="text-[11px] text-slate-400">Entry fee already paid • NFT payment only</p>
          </div>
        </div>
      </section>

      {/* AGC: Step 2: Match Found (from tournament data) */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase text-emerald-400 tracking-[0.2em]">MATCH FOUND</p>
          <span className="text-2xl">⚔️</span>
        </div>
        
        <div className="grid grid-cols-2 gap-1.5 mb-2">
          <div className="p-2 border border-purple-500/30">
            <div className="text-[9px] uppercase text-purple-300 tracking-wide mb-0.5">YOU</div>
            <div className="text-sm font-bold text-white truncate">{playerAddress.slice(0, 6)}...{playerAddress.slice(-4)}</div>
            <div className="text-[10px] text-slate-400">Lv {playerLevel}</div>
          </div>
          <div className="p-2 border border-blue-500/30">
            <div className="text-[9px] uppercase text-blue-300 tracking-wide mb-0.5">OPPONENT</div>
            <div className="text-sm font-bold text-white truncate">{opponentAddress.slice(0, 6)}...{opponentAddress.slice(-4)}</div>
            <div className="text-[10px] text-slate-400">Lv {opponentLevel}</div>
          </div>
        </div>

        <div className="border border-yellow-500/40 px-2 py-1.5">
          <div className="flex items-center gap-1.5 text-[10px] text-yellow-300">
            <span>💰</span>
            <span>Entry fee paid • Select NFTs (optional) • Winner advances</span>
          </div>
        </div>
      </section>

      {/* AGC: Step 3: Choose NFTs (Optional) - Only show if no buffs were pre-selected */}
      {!hasPaid && preSelectedBuffs.length === 0 && (
        <BuffSelector
          playerAddress={playerAddress}
          onBuffsSelected={handleBuffSelection}
          maxBuffs={2}
          disabled={isPaying}
          onNavigateToMarketplace={() => {
            if (onNavigateToPage) onNavigateToPage('marketplace')
          }}
          variant="compact"
        />
      )}

      {/* AGC: Show selected buffs if they were pre-selected */}
      {!hasPaid && preSelectedBuffs.length > 0 && selectedBuffs.length > 0 && (
        <section className="pb-2 border-b border-white/10">
          <div className="text-[10px] uppercase text-purple-300 tracking-[0.2em] mb-2">
            SELECTED BUFFS ({selectedBuffs.length}/2)
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedBuffs.map((buffId) => (
              <div key={buffId} className="px-3 py-1.5 bg-purple-500/20 border border-purple-500/40 rounded text-xs">
                {buffId === 0 && '🛡️ Shield'}
                {buffId === 1 && '⚔️ Pierce'}
                {buffId === 2 && '⚡ Haste'}
                {buffId === 3 && '❄️ Frost'}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* AGC: Step 4: Pay NFT button */}
      {!hasPaid && (
        <button
          onClick={handlePayNFT}
          disabled={isPaying || !battleId}
          className={`w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-3 px-6 rounded-lg transition-all shadow-lg disabled:cursor-not-allowed text-sm uppercase tracking-wide`}
        >
          {isPaying
            ? '⏳ Processing NFT Payment...'
            : !battleId
            ? '⏳ Waiting for match...'
            : (() => {
                const totalNFTs = selectedBuffs.length + selectedPotions.length
                return totalNFTs > 0
                  ? `🎮 Confirm Match (${totalNFTs} NFT${totalNFTs > 1 ? 's' : ''})`
                  : '🎮 Confirm Match (No NFTs)'
              })()
          }
        </button>
      )}

      {/* AGC: Step 5: Wait for opponent */}
      {hasPaid && !battleStarted && (
        <section className="pb-2 border-b border-white/10">
          <div className="text-center">
            <div className="text-6xl mb-4 animate-pulse" role="img" aria-hidden="true">
              ⏳
            </div>

            <h2 className="text-xl font-bold text-white mb-3">Waiting for Opponent...</h2>
            
            {opponentTimedOut ? (
              <div className="border border-green-500 p-3 mb-3">
                <div className="text-green-300 text-sm font-semibold">✅ Opponent Timed Out</div>
                <div className="text-gray-400 text-[11px]">You win by default! Returning to tournament...</div>
              </div>
            ) : (
              <>
                <p className="text-gray-300 mb-4 text-sm">
                  You've confirmed your match!<br/>
                  Waiting for opponent to confirm...
                </p>

                <div className="border border-orange-500 p-3">
                  <div className="text-orange-300 font-semibold mb-1 text-sm">⏱️ Timeout Timer</div>
                  <div className="text-white text-[11px] mb-2">
                    {timeRemaining > 0 ? (
                      <>
                        If opponent doesn't confirm within <span className="text-orange-400 font-mono">{timeRemaining}s</span>, you win by default.
                      </>
                    ) : (
                      <>Timeout reached - processing win...</>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      )}
    </main>
  )
}

